import axios, { AxiosInstance, AxiosError } from 'axios';
import { DeviceCodeResponse, DeviceStatusResponse } from '@/types/auth.types';
import { configService } from './configService';
import { createApiError } from '../types/errors.types';

let apiClient: AxiosInstance | null = null;

interface ApiErrorResponse {
  message?: string;
  code?: string;
  error?: string;
}

const getApiClient = async (): Promise<AxiosInstance> => {
  if (apiClient) {
    return apiClient;
  }

  const config = await configService.getConfig();
  
  apiClient = axios.create({
    baseURL: config.api.baseUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add response interceptor for error handling
  apiClient.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiErrorResponse>) => {
      // Convert axios errors to our ApiError type
      const message = error.response?.data?.message || 
                     error.response?.data?.error || 
                     error.message || 
                     'Unknown API error';
      
      const apiError = createApiError(
        message,
        error.response?.status,
        error.response?.data?.code || 'API_ERROR'
      );
      
      return Promise.reject(apiError);
    }
  );

  return apiClient;
};

export interface VerifyDeviceResponse {
  success: boolean;
  message: string;
}

export class AuthApi {
  static async requestDeviceCode(): Promise<DeviceCodeResponse> {
    const client = await getApiClient();
    const response = await client.post('/api/device/code');
    return response.data;
  }

  static async checkDeviceStatus(deviceCode: string): Promise<DeviceStatusResponse> {
    const client = await getApiClient();
    const response = await client.get('/api/device/status', {
      params: { device_code: deviceCode }
    });
    return response.data;
  }

  static async verifyDeviceCode(userCode: string, azureToken: string): Promise<VerifyDeviceResponse> {
    const client = await getApiClient();
    const response = await client.post('/api/device/verify', {
      user_code: userCode
    }, {
      headers: {
        'Authorization': `Bearer ${azureToken}`
      }
    });
    return response.data;
  }

  static async testProtectedEndpoint(token?: string): Promise<Response> {
    const config = await configService.getConfig();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return fetch(`${config.api.baseUrl}/api/test-protected`, {
      method: 'GET',
      headers
    });
  }
}

export default AuthApi;